import React, { useState } from 'react';
import axios from 'axios';
import { Button, Snackbar, Alert, Typography, Box, CircularProgress } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL;

const GenerateOTPButtonRequest = ({ requestId, userId }) => {
  const [otp, setOtp] = useState('');
  const [isOtpGenerated, setIsOtpGenerated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);

  const generateOTP = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/generate-otp-request/`, { request_id: requestId, user_id: userId });
      setOtp(response.data.OTP);
      setIsOtpGenerated(true);
      setMessage('OTP generated successfully!');
      setOpen(true);
    } catch (error) {
      setMessage('Error generating OTP');
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateOTP = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/generate-otp-request/`, { request_id: requestId, user_id: userId });
      setOtp(response.data.OTP);
      setMessage('OTP regenerated successfully!');
      setOpen(true);
    } catch (error) {
      setMessage('Error regenerating OTP');
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem',
        borderRadius: '12px',
        backgroundColor: '#333333', // Dark background for contrast
        color: '#ffffff', // Light text for readability
        boxShadow: 3,
        width: { xs: '100%', sm: '80%', md: '60%' }, // Responsive width
        maxWidth: '400px', // Max width for large screens
        margin: '0 auto', // Center the container
      }}
    >
      <Typography
        variant="h6"
        component="h1"
        sx={{
          textAlign: 'center',
          marginBottom: '1.5rem',
          fontWeight: 'bold',
          color: '#ffffff',
          letterSpacing: 4,
        }}
      >
        OTP Generation
      </Typography>

      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: '#e0e0e0',
          marginBottom: '1.5rem',
          letterSpacing: 3,
        }}
      >
        Click the button below to generate a new OTP for the booking request. If you have already generated one, you can regenerate it as well.
      </Typography>

      <Typography
        variant="h5"
        sx={{
          textAlign: 'center',
          color: '#ffffff',
          marginBottom: '1rem',
          fontWeight: '500',
          letterSpacing: 3,
        }}
      >
        OTP: {otp || 'Not Generated Yet'}
      </Typography>

      <Button
        variant="contained"
        color="primary"
        onClick={isOtpGenerated ? regenerateOTP : generateOTP}
        disabled={isLoading}
        sx={{
          marginTop: '1.5rem',
          background: 'linear-gradient(45deg, #1e90ff, #1c70ff)',
          color: '#fff',
          padding: '1rem 2rem',
          fontWeight: 'bold',
          textTransform: 'none',
          letterSpacing: 4,
          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
          borderRadius: '50px',
          boxShadow: '0 12px 18px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            background: 'linear-gradient(45deg, #1e90ff, #1c70ff)',
            transform: 'scale(1.05)',
            boxShadow: '0 18px 30px rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : isOtpGenerated ? 'Regenerate OTP' : 'Generate OTP'}
      </Button>

      {/* Snackbar for feedback */}
      <Snackbar open={open} autoHideDuration={6000} onClose={() => setOpen(false)}>
        <Alert onClose={() => setOpen(false)} severity={isOtpGenerated ? 'success' : 'error'} sx={{ width: '100%', letterSpacing: 4 }}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GenerateOTPButtonRequest;
